import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Info, GitBranch } from 'lucide-react';

const fmt = (val) => {
  if (val == null || val === 0) return '—';
  const n = Number(val);
  if (isNaN(n) || n === 0) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};

const TooltipBadge = ({ text }) => {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-block ml-1">
      <Info
        size={13}
        className="inline text-blue-400 cursor-pointer align-text-top"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      />
      {show && (
        <span className="absolute z-50 bottom-full left-0 mb-1 w-64 rounded bg-gray-800 text-white text-xs p-2 shadow-lg whitespace-normal">
          {text}
        </span>
      )}
    </span>
  );
};

/**
 * FairFactorTree — collapsible panel showing the FAIR-aligned quantification summary
 * for an assessment. Receives quantificationFactors from the assessment-risks API response.
 * Monte Carlo percentile bars live in MonteCarloSection (mc-v3.0 is the single source of truth
 * for those) — this panel only shows the FAIR formula breakdown and the deterministic EAL.
 *
 * quantificationFactors shape (all fields optional / nullable):
 *   scenarioCount, ealDeterministic, ealLow, ealHigh, mcP50, mcP75, mcP90, mcP95, mcP99,
 *   mcMean, mcStdDev, seed, iterations, simulationVersion, fairEngineVersion, correlationLimitationNote
 *
 * The headline shows the EAL range (ealLow → ealHigh): the sum of each risk's low bound to the
 * sum of each risk's high bound — the same impactLow/impactHigh range shown on the risk cards.
 */
// The engine's correlation note ("Scenarios assumed independent in mc-v3.2 (except the systemic-year
// multiplier ...)") is its audit record, kept with each simulation run. Executives read this panel,
// so it states the same limitation without the engine's internal terms.
export const CORRELATION_NOTE_TEXT = 'Risk scenarios are simulated independently of each other, except for a shared '
  + 'factor that models occasional high-loss years affecting all of them.';

const FairFactorTree = ({ quantificationFactors }) => {
  const [expanded, setExpanded] = useState(false);

  if (!quantificationFactors) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-400 flex items-center gap-2">
        <GitBranch size={14} />
        Financial risk quantification is being calculated — figures will appear here once the AI analysis completes (typically within a few minutes of assessment creation).
      </div>
    );
  }

  const {
    scenarioCount,
    totalRiskCount,
    ealDeterministic,
    ealLow,
    ealHigh,
    mcP50,
    fairEngineVersion,
    correlationLimitationNote,
  } = quantificationFactors;

  const enrichmentPending = totalRiskCount != null && scenarioCount < totalRiskCount;

  // Headline range: sum(lows) → sum(highs). Fall back to the deterministic point if the range
  // fields are absent (older payloads that predate ealLow/ealHigh).
  const hasRange = ealLow != null && ealHigh != null;
  const headlineLabel = hasRange
    ? `${fmt(ealLow)} – ${fmt(ealHigh)}+`
    : (ealDeterministic != null ? fmt(ealDeterministic) : null);

  return (
    <div className="border border-blue-200 rounded-lg overflow-hidden text-sm mb-3">
      {/* Header row */}
      <button
        className="w-full flex items-center justify-between px-4 py-2.5 bg-blue-50 hover:bg-blue-100 text-left transition-colors"
        onClick={() => setExpanded(p => !p)}
      >
        <span className="flex items-center gap-2 font-semibold text-blue-800">
          <GitBranch size={15} />
          FAIR-Aligned Quantification
          <span className="text-xs font-normal text-blue-500 ml-1">
            {enrichmentPending
              ? `${scenarioCount ?? 0} / ${totalRiskCount} risk profiles`
              : `${scenarioCount ?? 0} risk profile${scenarioCount !== 1 ? 's' : ''}`}
          </span>
        </span>
        <span className="flex items-center gap-3">
          {enrichmentPending && (
            <span className="text-xs text-amber-600 font-medium animate-pulse">Calculating…</span>
          )}
          {!enrichmentPending && headlineLabel != null && (
            <span className="text-xs text-orange-700 font-semibold">
              {headlineLabel}
            </span>
          )}
          {expanded ? <ChevronUp size={15} className="text-blue-500" /> : <ChevronDown size={15} className="text-blue-500" />}
        </span>
      </button>

      {/* Enrichment in-progress banner */}
      {enrichmentPending && (
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-700">
          <Info size={13} className="shrink-0" />
          <span>
            FAIR-Aligned enrichment in progress — <strong>{scenarioCount} of {totalRiskCount}</strong> risk profiles calculated.
            Amounts shown are partial. <strong>Refresh the page in 1–2 minutes</strong> to see complete portfolio figures.
          </span>
        </div>
      )}

      {expanded && (
        <div className="bg-white px-4 py-3 space-y-3">
          {/* FAIR formula notation */}
          <div className="text-xs text-gray-400 font-mono bg-gray-50 rounded px-2 py-1">
            EAL = LEF × (PLM + SLM) &nbsp;·&nbsp; LEF = TEF × VULN
            <TooltipBadge text="TEF = Contact Frequency × Probability of Action. VULN = sigmoid((TCap - CS + Exposure × ExposureWeight) × VulnSlope). LEF is stored unrounded; only EAL rounds at output." />
          </div>

          {/* FAIR-Aligned EAL vs Simulated Median (P50) — two columns */}
          <div className="grid grid-cols-2 gap-2 border-b border-gray-100 pb-2">
            <div className="bg-gray-50 rounded px-2 py-1.5">
              <div className="text-xs text-gray-500 flex items-center gap-1">
                FAIR-Aligned EAL
                <TooltipBadge text="The scenario-weighted expected annual loss from FAIR-Aligned factor inputs (deterministic point estimate). The headline range spans the sum of each risk's low EAL bound to the sum of each risk's high EAL bound." />
              </div>
              <div className="text-sm font-bold text-gray-800 mt-0.5">{fmt(ealDeterministic)}</div>
            </div>
            <div className="bg-gray-50 rounded px-2 py-1.5">
              <div className="text-xs text-gray-500 flex items-center gap-1">
                Simulated Median (P50)
                <TooltipBadge text="The Monte Carlo median (P50) accounts for stochastic variance across simulated years." />
              </div>
              <div className="text-sm font-bold text-gray-800 mt-0.5">{fmt(mcP50)}</div>
            </div>
          </div>

          {/* Methodology note: information, not a warning, so neutral rather than amber. */}
          {correlationLimitationNote && (
            <div className="flex items-start gap-1.5 bg-gray-50 border border-gray-200 rounded px-2 py-1.5 text-xs text-gray-600"
              data-testid="fair-correlation-note">
              <Info size={12} className="mt-0.5 shrink-0" />
              {CORRELATION_NOTE_TEXT}
            </div>
          )}

          {/* Engine version footer */}
          <div className="text-xs text-gray-300 text-right">
            {fairEngineVersion ?? 'fair-factor-v1.0'}
          </div>
        </div>
      )}
    </div>
  );
};

export default FairFactorTree;
